import { Brand } from '@awazevr/design-system.components.awaze-theme-provider'
import { Image } from '@awazevr/design-system.compositions.property-list/dist/property-list.types'
import { useSearchContextState } from '@awazevr/mfe-common.contexts.search'
import { TmsContextType } from '@awazevr/mfe-common.contexts.tms'
import type {
  ImagesItem,
  PropertyImageResponse,
} from '@awazevr/mfe-common.types-and-mocks.site-search-service/dist/types/properties.types'
import { FormattedAmount } from '@awazevr/mfe-common.utils.locale-formatting'

import { OPTIMIZELY_EVENTS } from '@/constants'
import { OptimizelyDecisionsProps } from '@/context/optimizely-decisions-context'
import { usePublicAssetUrl } from '@/utils/helpers'
import { isNovasolPoweredBrand } from '@/utils/isNovasolPoweredBrand'
import { trackClientSideOptimizelyEvent } from '@/utils/optimizely/trackOptimizelyEvent'
import { formatPropertyImageUrl } from '@/utils/propertyImageUrl'

export const removeFloorplanImagesFromImageArray = (
  images: PropertyImageResponse,
): ImagesItem[] =>
  images.filter((image) => !image.assetId.includes('floorplan'))

export const formatUrlsForImageArray = (
  images: PropertyImageResponse,
): Image[] => {
  return images.map((image) => {
    return {
      imageURL: formatPropertyImageUrl(image.assetId),
      imgAlt: image.caption,
    }
  })
}

export const carouselInteractFactory = (
  imageArray: Array<Image | ImagesItem>,
  displayName: string,
  tms: TmsContextType,
  code: string | number,
) => {
  return (
    event: 'navigate' | 'open' | 'close',
    index?: number,
    nextOrPrevious?: 'next' | 'previous',
  ): void => {
    tms.updateDataLayer('link', 'image carousel-shift', {
      event_name: 'image carousel-shift',
      property_code: code,
      ...(event === 'navigate' && {
        carousel_total_images: imageArray.length,
        carousel_current_image_index: index ? index + 1 : 1,
        carousel_current_image_text: `search MFE - ${displayName}`,
        carousel_product_id: code,
      }),
      ...(nextOrPrevious && {
        carousel_direction: nextOrPrevious,
      }),
    })
  }
}

export const getDepositPriceFormattingCallback = (
  brand: Brand,
  formatCurrencyToObject: (value: number) => FormattedAmount,
): ((amount: number, currencySymbol: string) => string) | undefined => {
  if (isNovasolPoweredBrand(brand)) {
    return (amount, currencySymbol): string => {
      const formattedDepositObj = formatCurrencyToObject(amount ?? 0)

      if (formattedDepositObj.currencySignPosition === 'before') {
        return `${currencySymbol}${formattedDepositObj.formattedAmount}`
      } else {
        return `${formattedDepositObj.formattedAmount}${currencySymbol}`
      }
    }
  }
}

export type MerchBannerProps = {
  brand: Brand
  variant: 'control' | 'variant_a'
  locale: string
  optiContext: OptimizelyDecisionsProps
}

export const MerchBanner = ({
  brand,
  variant,
  locale,
  optiContext,
}: MerchBannerProps): React.ReactNode => {
  const { searchCriteria } = useSearchContextState()

  if (variant === 'control') {
    return null
  }

  let prefix,
    ext: string = ''

  switch (brand) {
    case 'cottages.com':
      prefix = 'cdc'
      ext = 'jpg'
      break
    case 'hoseasons':
      prefix = 'hos'
      ext = 'jpg'
      break
    case 'novasol':
      if (locale == 'de') prefix = 'nov/de'
      if (locale == 'da') prefix = 'nov/dk'
      ext = 'webp'
      break
  }

  if (!prefix || !ext) {
    return null
  }

  let href: string | undefined
  if (brand === 'hoseasons' && searchCriteria) {
    const p = new URLSearchParams(Object.entries(searchCriteria))
    p.set('accommodationType', 'cottages')
    p.set('experiment', 'banner')
    href = `?${p.toString()}`
  }

  const handleClick = () => {
    trackClientSideOptimizelyEvent(
      OPTIMIZELY_EVENTS.MERCH_POD_CLICK,
      optiContext,
    )
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLPictureElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleClick()
    }
  }

  const desktopBannerUrl = usePublicAssetUrl(
    `/brand/banner-assets/${prefix}/lgbanner.${ext}`,
  )
  const tabletBannerUrl = usePublicAssetUrl(
    `/brand/banner-assets/${prefix}/mdbanner.${ext}`,
  )
  const mobileBannerUrl = usePublicAssetUrl(
    `/brand/banner-assets/${prefix}/smbanner.${ext}`,
  )

  if (href) {
    return (
      <a
        href={href}
        onClick={handleClick}
        data-testid="merch-banner"
        aria-label="Search for cottages"
        style={{ maxWidth: '100%', overflow: 'hidden', display: 'block' }}
      >
        <picture>
          <source media="(min-width: 1240px)" srcSet={desktopBannerUrl} />
          <source media="(min-width: 768px)" srcSet={tabletBannerUrl} />
          <img
            src={mobileBannerUrl}
            alt=""
            style={{ width: '100%', height: 'auto', display: 'block' }}
          />
        </picture>
      </a>
    )
  }

  return (
    <button
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      data-testid="merch-banner"
    >
      <picture>
        <source media="(min-width: 1240px)" srcSet={desktopBannerUrl} />
        <source media="(min-width: 768px)" srcSet={tabletBannerUrl} />
        <img src={mobileBannerUrl} alt="" />
      </picture>
    </button>
  )
}
